import { useState, useEffect } from 'react'
import { Box } from '@mui/material'

import TestExecutionControls from './TestExecutionControls'
import NewTestCaseDialog from './NewTestCaseDialog'
import TestCaseDetail from '../development/TestCase/TestCaseDetail'
import TestCaseExecutionResult from '../development/TestCase/TestCaseExecutionResult'
import { useTranslationMapDetailsContext } from '../../contexts/TranslationMapDetailsContext'
import {
  useRunTranslationTestCase,
  useCreateTranslationTestCase,
  useSearchTranslationTestCases,
  useDeleteTranslationTestCase,
  useCloneTranslationTestCase,
} from '../../api'
import { DevelopmentTranslationTestCase } from '../../types'
import { TranslationTestCaseDetail } from 'common/types/kraken-core/TranslationMap'
import { toast } from 'atoms/Toast'

export interface TestingViewProps {
  onCreateTest?: () => void
  onRunAllTests?: () => void
  isCreatingTest?: boolean
  totalTests?: number
}

export default function TestingView({ onCreateTest, onRunAllTests }: TestingViewProps = {}) {
  const { translationMap } = useTranslationMapDetailsContext()

  // State for tests
  const [tests, setTests] = useState<DevelopmentTranslationTestCase[]>([])
  const [showNewTestCaseDialog, setShowNewTestCaseDialog] = useState(false)
  const [showEditTestCaseDialog, setShowEditTestCaseDialog] = useState(false)
  const [showExecutionResultDialog, setShowExecutionResultDialog] = useState(false)
  const [selectedTestCaseId, setSelectedTestCaseId] = useState<string | undefined>()
  const [selectedTestForExecution, setSelectedTestForExecution] = useState<
    DevelopmentTranslationTestCase | undefined
  >()

  // API hooks
  const { mutateAsync: runTestMutationAsync } = useRunTranslationTestCase()
  const { mutate: createTestCase, isLoading: isCreatingTestCase } = useCreateTranslationTestCase()
  const { mutate: deleteTestCase, isLoading: isDeletingTestCase } = useDeleteTranslationTestCase()
  const { mutate: cloneTestCase, isLoading: isCloningTestCase } = useCloneTranslationTestCase()

  // Load existing test cases
  const {
    data: testCasesData,
    isLoading: isLoadingTestCases,
    refetch: refetchTestCases,
  } = useSearchTranslationTestCases(
    { translationMapId: translationMap?.id || '' },
    { enabled: !!translationMap?.id }
  )

  // Load test cases when data is available
  useEffect(() => {
    if (testCasesData?.results) {
      const loadedTests: DevelopmentTranslationTestCase[] = testCasesData.results
        .map(testCase => ({
          testCaseDetail: testCase,
          isRunning: false,
          execution: undefined,
        }))
        .sort((a, b) => {
          const nameA = a.testCaseDetail?.description || ''
          const nameB = b.testCaseDetail?.description || ''
          return nameA.localeCompare(nameB)
        })
      setTests(loadedTests)
    }
  }, [testCasesData])

  // Early return if context values are not available
  if (!translationMap) {
    return <div>Loading...</div>
  }

  // Show loading state while fetching test cases
  if (isLoadingTestCases) {
    return <div>Loading test cases...</div>
  }

  // Test execution functions
  const handleRunAllTests = async () => {
    if (tests.length === 0) {
      toast.info('No tests to run')
      return
    }

    for (const test of tests) {
      if (test.testCaseDetail?.id) {
        await runTest(test.testCaseDetail.id)
      }
    }
  }

  // Use passed handlers or local ones
  const handleCreateTestClick = onCreateTest || (() => setShowNewTestCaseDialog(true))
  const handleRunAllTestsClick = onRunAllTests || handleRunAllTests

  const runTest = async (testId: string) => {
    try {
      const response = await runTestMutationAsync({
        translationMapId: translationMap.id,
        translationTestCaseId: testId,
      })

      const resultExecution = {
        success: response.success,
        details: response.details,
        expected_payload: response.expected_payload
          ? atob(String(response.expected_payload))
          : undefined,
        translated_payload: response.translated_payload
          ? atob(String(response.translated_payload))
          : undefined,
      }

      setTests(prev =>
        prev.map(test =>
          test.testCaseDetail?.id === testId
            ? { ...test, isRunning: false, execution: resultExecution }
            : test
        )
      )

      if (!resultExecution.success) {
        toast.error('Test failed')
      }
    } catch (error) {
      toast.error(`Test execution failed: ${error}`)
      setTests(prev =>
        prev.map(test =>
          test.testCaseDetail?.id === testId ? { ...test, isRunning: false } : test
        )
      )
    }
  }

  const handleCreateTestCase = (testCase: TranslationTestCaseDetail) => {
    createTestCase(
      {
        translationMapId: translationMap.id,
        translationTestCase: testCase,
      },
      {
        onSuccess: created => {
          // Refresh the test cases list to get the latest data (this will trigger the useEffect that sorts them)
          refetchTestCases()
          setShowNewTestCaseDialog(false)
          toast.success('Test case created successfully!')
        },
        onError: error => {
          toast.error(`Failed to create test case: ${error}`)
        },
      }
    )
  }

  const handleDeleteTestCase = (testId: string) => {
    deleteTestCase(
      {
        translationMapId: translationMap.id,
        translationTestCaseId: testId,
      },
      {
        onSuccess: () => {
          // Remove the test from local state immediately for better UX
          setTests(prev => prev.filter(test => test.testCaseDetail?.id !== testId))
          toast.success('Test case deleted successfully!')
        },
        onError: error => {
          toast.error(`Failed to delete test case: ${error}`)
        },
      }
    )
  }

  const handleEditTestCase = (testId: string) => {
    setSelectedTestCaseId(testId)
    setShowEditTestCaseDialog(true)
  }

  const handleShowExecutionResult = (test: DevelopmentTranslationTestCase) => {
    setSelectedTestForExecution(test)
    setShowExecutionResultDialog(true)
  }

  const handleCloseEditDialog = () => {
    setShowEditTestCaseDialog(false)
    setSelectedTestCaseId(undefined)
  }

  const handleCloseExecutionResultDialog = () => {
    setShowExecutionResultDialog(false)
    setSelectedTestForExecution(undefined)
  }

  const handleTestCaseUpdated = (updated: TranslationTestCaseDetail) => {
    if (!updated) return
    setTests(prev => {
      const updatedTests = prev.map(test => {
        if (test.testCaseDetail?.id === updated.id) {
          return {
            ...test,
            testCaseDetail: {
              ...test.testCaseDetail,
              description: updated.description,
              expected_result: updated.expectation_definition?.result as any,
            },
          }
        }
        return test
      })

      // Sort alphabetically by description
      return updatedTests.sort((a, b) => {
        const nameA = a.testCaseDetail?.description || ''
        const nameB = b.testCaseDetail?.description || ''
        return nameA.localeCompare(nameB)
      })
    })
    setShowEditTestCaseDialog(false)
    setSelectedTestCaseId(undefined)
  }

  const handleCloneTestCase = (testId: string) => {
    cloneTestCase(
      {
        translationMapId: translationMap.id,
        translationTestCaseId: testId,
      },
      {
        onSuccess: cloned => {
          // Refresh the test cases list to get the latest data (this will trigger the useEffect that sorts them)
          refetchTestCases()
          toast.success('Test case cloned successfully!')
        },
        onError: error => {
          toast.error(`Failed to clone test case: ${error}`)
        },
      }
    )
  }

  return (
    <Box>
      {/* Test Summary Cards */}

      {/* Test Execution Controls */}
      <TestExecutionControls
        tests={tests}
        onRunAllTests={handleRunAllTestsClick}
        onRunTest={runTest}
        onCreateTest={handleCreateTestClick}
        onEditTest={handleEditTestCase}
        onDeleteTest={handleDeleteTestCase}
        onCloneTest={handleCloneTestCase}
        onShowExecutionResult={handleShowExecutionResult}
        isRunning={tests.some(test => test.isRunning)}
        isCreatingTest={isCreatingTestCase}
      />

      {/* Status Messages */}

      {/* Dialogs */}
      <NewTestCaseDialog
        open={showNewTestCaseDialog}
        onClose={() => setShowNewTestCaseDialog(false)}
        onCreate={handleCreateTestCase}
        isCreating={isCreatingTestCase}
      />

      {/* Edit Test Case Dialog */}
      <TestCaseDetail
        open={showEditTestCaseDialog}
        onClose={handleCloseEditDialog}
        testCaseId={selectedTestCaseId}
        onUpdated={handleTestCaseUpdated}
      />

      {/* Execution Result Dialog */}
      {selectedTestForExecution && (
        <TestCaseExecutionResult
          open={showExecutionResultDialog}
          onClose={handleCloseExecutionResultDialog}
          localTest={selectedTestForExecution}
        />
      )}
    </Box>
  )
}
